import { useEffect, useRef, useState } from "react";
import { QrCodeIcon } from "@heroicons/react/24/outline";
import { CodeScanner } from "@/components/CodeScanner";
import { NumericKeypad } from "@/components/NumericKeypad";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { parseArtikelEtikettScan } from "@/lib/artikelEtikett";
import { resolveArtikelFromEtikettScan, type ScannedMaArtikel } from "@/lib/artikelEtikettScan";
import { parseGermanDecimal } from "@/lib/germanDecimal";

export interface MaArtikelScanAdd {
  text: string;
  menge: string;
  einheit: string;
  artikel_id: number;
  hero_nr: string | null;
  hero_unit: string | null;
  ean: string | null;
}

export interface MaArtikelScanDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (position: MaArtikelScanAdd) => void;
}

type Phase = "scan" | "loading" | "qty";

function formatMengeInput(value: number): string {
  return String(value).replace(".", ",");
}

export function MaArtikelScanDialog({ open, onClose, onAdd }: MaArtikelScanDialogProps) {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [phase, setPhase] = useState<Phase>("scan");
  const [scanError, setScanError] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState("");
  const [scanned, setScanned] = useState<ScannedMaArtikel | null>(null);
  const [menge, setMenge] = useState("1");
  const [mengeError, setMengeError] = useState<string | null>(null);
  const mengeRef = useRef<HTMLInputElement | null>(null);
  const lastRawRef = useRef<{ raw: string; at: number } | null>(null);
  const resolveGenRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setPhase("scan");
      setScanError(null);
      setManualCode("");
      setScanned(null);
      setMenge("1");
      setMengeError(null);
      lastRawRef.current = null;
      resolveGenRef.current += 1;
    }
  }, [open]);

  useEffect(() => {
    if (open && phase === "qty" && !isMobile) {
      const id = window.setTimeout(() => {
        mengeRef.current?.focus();
        mengeRef.current?.select();
      }, 50);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open, phase, isMobile]);

  async function resolveRaw(raw: string) {
    const text = raw.trim();
    if (!text) return;
    const now = Date.now();
    const last = lastRawRef.current;
    if (last && last.raw === text && now - last.at < 1600) return;
    lastRawRef.current = { raw: text, at: now };
    if (!parseArtikelEtikettScan(text)) {
      setScanError("Kein Artikel-QR erkannt. Bitte das Etikett erneut vor die Kamera halten.");
      return;
    }

    const gen = resolveGenRef.current + 1;
    resolveGenRef.current = gen;
    setScanError(null);
    setPhase("loading");
    try {
      const result = await resolveArtikelFromEtikettScan(text);
      if (resolveGenRef.current !== gen) return;
      setScanned(result);
      setMenge("1");
      setMengeError(null);
      setPhase("qty");
    } catch (err) {
      if (resolveGenRef.current !== gen) return;
      setScanned(null);
      setPhase("scan");
      setScanError(
        err instanceof Error ? err.message : "Artikel konnte nicht geladen werden.",
      );
    }
  }

  function submitMenge() {
    if (!scanned?.article.artikel_id) return;
    const qty = parseGermanDecimal(menge);
    if (qty == null || qty <= 0) {
      setMengeError("Bitte eine Menge größer als 0 eingeben.");
      mengeRef.current?.focus();
      return;
    }
    onAdd({
      text: scanned.text,
      menge: formatMengeInput(qty),
      einheit: scanned.einheit,
      artikel_id: scanned.article.artikel_id,
      hero_nr: scanned.article.hero_nr,
      hero_unit: scanned.article.hero_unit ?? scanned.article.einheit ?? null,
      ean: scanned.article.ean,
    });
    setScanned(null);
    setMenge("1");
    setMengeError(null);
    setManualCode("");
    setScanError(null);
    setPhase("scan");
  }

  const article = scanned?.article;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Artikel-QR scannen"
      description="Etikett vor die Kamera halten. Danach die Menge eingeben – danach kannst du direkt den nächsten Code scannen."
      size="md"
      fullScreenMobile
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          {phase === "qty" ? (
            <>
              <Button type="button" variant="ghost" onClick={() => setPhase("scan")}>
                Anderer Code
              </Button>
              <Button type="button" onClick={submitMenge}>
                Hinzufügen
              </Button>
            </>
          ) : (
            <Button type="button" variant="ghost" onClick={onClose}>
              Fertig
            </Button>
          )}
        </div>
      }
    >
      {phase === "qty" && article ? (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            submitMenge();
          }}
        >
          <div className="rounded-ios bg-fill-tertiary px-3 py-2">
            <p className="text-sm font-semibold text-label">{scanned?.text}</p>
            <p className="mt-0.5 text-xs text-label-secondary">
              {[article.hero_nr ? `Hero ${article.hero_nr}` : null, scanned?.einheit]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          {isMobile ? (
            <div className="space-y-3">
              <div className="rounded-ios bg-fill-secondary px-3 py-3 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                  Menge{scanned?.einheit ? ` (${scanned.einheit})` : ""}
                </p>
                <p className="mt-1 text-4xl font-semibold tabular-nums text-label">
                  {menge || "0"}
                </p>
                {mengeError ? <p className="mt-1 text-xs text-status-red">{mengeError}</p> : null}
              </div>
              <NumericKeypad
                value={menge}
                onChange={(next) => {
                  setMenge(next);
                  setMengeError(null);
                }}
              />
            </div>
          ) : (
            <TextInput
              ref={mengeRef}
              id="ma-scan-menge"
              label={`Menge${scanned?.einheit ? ` (${scanned.einheit})` : ""}`}
              inputMode="decimal"
              value={menge}
              error={mengeError}
              onChange={(e) => {
                setMenge(e.target.value);
                setMengeError(null);
              }}
            />
          )}
        </form>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <CodeScanner
              active={open && phase === "scan"}
              onDetected={(raw) => {
                void resolveRaw(raw);
              }}
            />
            {phase === "loading" ? (
              <div className="absolute inset-0 flex items-center justify-center rounded-ios bg-black/50 text-white">
                <Spinner className="h-6 w-6" />
              </div>
            ) : null}
          </div>
          {scanError ? <p className="text-sm text-status-red">{scanError}</p> : null}
          <form
            className="flex items-stretch gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void resolveRaw(manualCode);
            }}
          >
            <input
              id="ma-scan-manual"
              className="eh-input min-w-0 flex-1"
              placeholder="Oder Link / Code einfügen"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              autoComplete="off"
            />
            <Button
              type="submit"
              variant="secondary"
              className="shrink-0"
              disabled={!manualCode.trim() || phase === "loading"}
              leftIcon={<QrCodeIcon className="h-4 w-4" />}
            >
              Übernehmen
            </Button>
          </form>
        </div>
      )}
    </Dialog>
  );
}
